import React, { useCallback, useMemo } from 'react';
import { EuiForm, EuiSpacer, EuiBasicTable } from '@elastic/eui';
import FormGroupDescription from '../../../components/formGroupDescription';
import ButtonGroup from '../../../components/Form/ButtonGroup';
import CheckBoxField from '../../../components/Form/CheckBoxField';
import NumberField from '../../../components/Form/NumberField';
import { useDispatch, useSelector } from 'react-redux';
import { outletSettingsAPI } from '../../../api/outlet/outletSettingsAPI';
import useOutletFormValues from '../Hooks/useOutletFormValues';
import useFormActionsHandler from '../../../hooks/useFormActionsHandler';

const OtherSettings = () => {
    const dispatch = useDispatch();
    const restaurantDescriptions = useSelector((state) => state.auth.restaurantDescriptions);

    const updateFormValues = useCallback((data) => {
        return {
            enable_auto_accept_order: data.settings.enable_auto_accept_order,
            show_best_selling_category: data?.settings?.show_best_selling_category,
            show_offer_suggestion: data?.settings?.show_offer_suggestion,
            has_featured_item_section: data?.settings?.has_featured_item_section,
            whats_app_enabled: data?.settings?.whats_app_enabled,
            whatsapp_loyalty_earned: data?.settings?.whatsapp_loyalty_earned,
            whatsapp_loyalty_expiring: data?.settings?.whatsapp_loyalty_expiring,
            whatsapp_loyalty_redeemed: data?.settings?.whatsapp_loyalty_redeemed,
            whatsapp_loyalty_reminder: data?.settings?.whatsapp_loyalty_reminder,
            whatsapp_order_cancelled: data?.settings?.whatsapp_order_cancelled,
            whatsapp_order_confirmed: data?.settings?.whatsapp_order_confirmed,
            whatsapp_order_delivered: data?.settings?.whatsapp_order_delivered,
            whatsapp_order_dispatched: data?.settings?.whatsapp_order_dispatched,
            whatsapp_order_placed: data?.settings?.whatsapp_order_placed,
            whatsapp_order_ready: data?.settings?.whatsapp_order_ready,
            whatsapp_refund_initiated: data?.settings?.whatsapp_refund_initiated,
            enable_auto_cancel_inactive_orders: data?.settings?.enable_auto_cancel_inactive_orders ?? false,
            auto_cancel_buffer_minutes: data?.settings?.auto_cancel_buffer_minutes ?? 60,
            enable_auto_refund_on_cancel: data?.settings?.enable_auto_refund_on_cancel ?? false,
        };
    }, []);

    const { defaultValues, setDefaultValues, onSuccessCallback } = useOutletFormValues({ updateFormValues });

    const onFormSaveApi = useCallback(
        async (data) => {
            // Remove disabled fields from the data object
            const filteredData = { ...data };
            const disabledFields = [
                'whatsapp_order_placed',
                'whatsapp_order_delivered',
                'whatsapp_order_cancelled',
                'whatsapp_refund_initiated',
            ];
            disabledFields.forEach((field) => {
                delete filteredData[field];
            });

            const response = await dispatch(outletSettingsAPI('other_settings', filteredData));
            if (response.success) setDefaultValues(updateFormValues(response.outlet));
            return response;
        },
        [dispatch, updateFormValues, setDefaultValues]
    );

    const { control, watch, setValue, errors } = useFormActionsHandler({ onFormSaveApi, defaultValues, onSuccessCallback });

    const WHATSAPP_NOTIFICATION_CONFIG = {
        ORDER_NOTIFICATIONS: [
            { id: 'whatsapp_order_placed', label: 'Order Placed', field: 'whatsapp_order_placed', isReadOnly: true },
            { id: 'whatsapp_order_confirmed', label: 'Order Confirmed', field: 'whatsapp_order_confirmed', isReadOnly: false },
            { id: 'whatsapp_order_ready', label: 'Order Ready', field: 'whatsapp_order_ready', isReadOnly: false },
            { id: 'whatsapp_order_dispatched', label: 'Order Dispatched', field: 'whatsapp_order_dispatched', isReadOnly: false },
            { id: 'whatsapp_order_delivered', label: 'Order Delivered', field: 'whatsapp_order_delivered', isReadOnly: true },
            { id: 'whatsapp_order_cancelled', label: 'Order Cancelled', field: 'whatsapp_order_cancelled', isReadOnly: true },
        ],
        PAYMENT_NOTIFICATIONS: [
            { id: 'whatsapp_refund_initiated', label: 'Refund Initiated', field: 'whatsapp_refund_initiated', isReadOnly: true },
        ],
        LOYALTY_NOTIFICATIONS: [
            { id: 'whatsapp_loyalty_earned', label: 'Loyalty Earned', field: 'whatsapp_loyalty_earned', isReadOnly: false },
            { id: 'whatsapp_loyalty_redeemed', label: 'Loyalty Redeemed', field: 'whatsapp_loyalty_redeemed', isReadOnly: false },
            { id: 'whatsapp_loyalty_expiring', label: 'Loyalty Expiring', field: 'whatsapp_loyalty_expiring', isReadOnly: false },
            { id: 'whatsapp_loyalty_reminder', label: 'Loyalty Reminder', field: 'whatsapp_loyalty_reminder', isReadOnly: false },
        ],
    };

    const whatsappNotificationItems = useMemo(() => 
        Object.values(WHATSAPP_NOTIFICATION_CONFIG).flat(),
        []
    );

    const notificationTableColumns = useMemo(() => [
        {
            field: 'label',
            name: 'Notification Type',
            width: '70%',
            truncateText: false,
        },
        {
            field: 'enabled',
            name: 'Enabled',
            width: '30%',
            align: 'center',
            render: (_, item) => (
                <CheckBoxField
                    name={item.field}
                    control={control}
                    setValue={setValue}
                    defaultValue={watch(item.field)}
                    isDisabled={item.isReadOnly}
                />
            ),
        },
    ], [control, setValue, watch]);

    // Prepare table data with current form values
    const notificationTableData = useMemo(() => 
        whatsappNotificationItems.map(item => ({
            ...item,
            enabled: watch(item.field),
        })),
        [whatsappNotificationItems, watch]
    );
    
    return (
        <EuiForm component='form'>
            <FormGroupDescription
                title={restaurantDescriptions?.auto_accept?.display_name}
                description={restaurantDescriptions?.auto_accept?.description}
            >
                <ButtonGroup
                    idSelected={watch('enable_auto_accept_order') ? '1' : '0'}
                    color='primary'
                    control={control}
                    setValue={setValue}
                    name='enable_auto_accept_order'
                    watch={watch}
                    label={'Enable auto accept order'}
                />
            </FormGroupDescription>
            <EuiSpacer />
            <FormGroupDescription
                title={'Show Best Seller Category'}
                description={'Show Best Seller Category In Hom-Screen'}
            >
                <ButtonGroup
                    idSelected={watch('show_best_selling_category') ? '1' : '0'}
                    color='primary'
                    control={control}
                    setValue={setValue}
                    name='show_best_selling_category'
                    watch={watch}
                    label={'Enable Best Seller Category'}
                />
            </FormGroupDescription>
            <EuiSpacer />
            <FormGroupDescription
                title={'Show Offer Suggestions'}
                description={'Show Offer Suggestions In Category detail Screen'}
            >
                <ButtonGroup
                    idSelected={watch('show_offer_suggestion') ? '1' : '0'}
                    color='primary'
                    control={control}
                    setValue={setValue}
                    name='show_offer_suggestion'
                    watch={watch}
                    label={'Enable Offer Suggestions'}
                />
            </FormGroupDescription>
            <FormGroupDescription
                title={'Show Feature Section'}
                description={'Show Featured Section In Category detail Screen'}
            >
                <ButtonGroup
                    idSelected={watch('has_featured_item_section') ? '1' : '0'}
                    color='primary'
                    control={control}
                    setValue={setValue}
                    name='has_featured_item_section'
                    watch={watch}
                    label={'Enable Featured Suggestions'}
                />
            </FormGroupDescription>
            <EuiSpacer />
            <FormGroupDescription
                title={'WhatsApp Notifications'}
                description={'Enable WhatsApp notifications to send automated messages to customers'}
            >
                <ButtonGroup
                    idSelected={watch('whats_app_enabled') ? '1' : '0'}
                    color='primary'
                    control={control}
                    setValue={setValue}
                    name='whats_app_enabled'
                    watch={watch}
                    label={'Enable WhatsApp Notifications'}
                />
            </FormGroupDescription>
            {Boolean(watch('whats_app_enabled')) && (
                <>
                    <FormGroupDescription
                        title={'Notification Types'}
                        description={'Select which notifications to send to customers via WhatsApp'}
                    >
                        <EuiBasicTable
                            items={notificationTableData}
                            columns={notificationTableColumns}
                            responsive={false}
                            compressed={true}
                            tableLayout='fixed'
                        />
                    </FormGroupDescription>
                </>
            )}
            <EuiSpacer />
            <FormGroupDescription
                title={'Auto-Cancel Inactive Orders'}
                description={'Automatically cancel orders that remain inactive beyond the specified time'}
            >
                <ButtonGroup
                    idSelected={watch('enable_auto_cancel_inactive_orders') ? '1' : '0'}
                    color='primary'
                    control={control}
                    setValue={setValue}
                    name='enable_auto_cancel_inactive_orders'
                    watch={watch}
                    label={'Enable Auto-Cancel Inactive Orders'}
                />
            </FormGroupDescription>
            {Boolean(watch('enable_auto_cancel_inactive_orders')) && (
                <>
                    <FormGroupDescription
                        title={'Auto Cancel Buffer Time'}
                        description={'Number of minutes an order can remain inactive before being auto-cancelled (minimum 60)'}
                    >
                        <NumberField
                            label={'Buffer Time (minutes)'}
                            name='auto_cancel_buffer_minutes'
                            control={control}
                            errors={errors}
                            placeholder='e.g. 60'
                            min={60}
                            rules={{
                                required: 'Buffer time is required',
                                min: { value: 60, message: 'Must be at least 60 minutes' },
                                validate: (v) => Number.isInteger(Number(v)) || 'Must be a whole number',
                            }}
                        />
                    </FormGroupDescription>
                    <FormGroupDescription
                        title={'Auto Refund on Cancel'}
                        description={'Automatically initiate a refund when an order is auto-cancelled'}
                    >
                        <ButtonGroup
                            idSelected={watch('enable_auto_refund_on_cancel') ? '1' : '0'}
                            color='primary'
                            control={control}
                            setValue={setValue}
                            name='enable_auto_refund_on_cancel'
                            watch={watch}
                            label={'Enable Auto Refund on Cancel'}
                        />
                    </FormGroupDescription>
                </>
            )}
            <EuiSpacer />
        </EuiForm>
    );
};

export default OtherSettings;
